import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, LogOut, Pencil } from "lucide-react";
import { usePrefersReducedMotion } from "./shared";

/* ------------------------------------------------------------------ */
/* Chat shell + bubbles                                                */
/* ------------------------------------------------------------------ */

export function ChatLayout({ children, scrollRef, onSkip, onSignout }) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-lm-tint/40 via-lm-bg to-lm-bg">
      {/* Full-width navbar — marketing style, dark green with the logo image */}
      <header className="sticky top-0 z-40 bg-[color:var(--lm-dark)] border-b border-[color:var(--lm-dark-border)]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="Leadmato home">
            <img
              src="/leadmato-logo.png"
              alt="Leadmato"
              className="h-8 md:h-9 w-auto max-w-[170px] object-contain select-none"
              draggable="false"
            />
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <button
              type="button"
              onClick={onSkip}
              data-testid="onboarding-skip"
              className="text-sm text-white/70 hover:text-white transition-colors px-2 py-1.5"
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={onSignout}
              data-testid="onboarding-signout"
              className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors px-2 py-1.5"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Contained chat window — floats below the navbar, spaced from all sides */}
      <div className="flex-1 min-h-0 flex justify-center px-3 sm:px-5 md:px-8 py-4 md:py-6">
        <div className="w-full max-w-2xl flex flex-col rounded-2xl border border-lm-border bg-lm-surface shadow-[0_24px_60px_-20px_rgba(15,85,53,0.25)] overflow-hidden">
          {/* Window title bar */}
          <div className="px-4 md:px-5 h-14 flex items-center gap-2.5 border-b border-lm-border bg-lm-surface/90 backdrop-blur shrink-0">
            <span className="w-8 h-8 rounded-full bg-lm-primary text-white flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4" />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-lm-text">Leadmato setup</div>
              <div className="flex items-center gap-1.5 text-[11px] text-lm-text3">
                <span className={"w-1.5 h-1.5 rounded-full bg-green-500" + (reducedMotion ? "" : " animate-pulse")} />
                online — replies instantly
              </div>
            </div>
          </div>

          {/* Scrollable conversation canvas */}
          <main
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto bg-gradient-to-b from-lm-bg to-lm-tint/20 scroll-smooth"
          >
            <div className="px-3 md:px-5 py-6 space-y-4">
              {children}
              <div className="h-2" />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export function Assistant({ children }) {
  return (
    <div className="flex items-start gap-3 animate-[fadeIn_0.3s_ease]">
      <div className="w-8 h-8 rounded-full bg-lm-primary text-white flex items-center justify-center shrink-0 mt-0.5">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-lm-border bg-lm-surface px-4 py-3 text-lm-text leading-relaxed shadow-sm">
        {children}
      </div>
    </div>
  );
}

export function UserReply({ children, mono, onEdit }) {
  return (
    <div className="flex justify-end items-center gap-2 animate-[fadeIn_0.3s_ease]">
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1 text-xs text-lm-text3 hover:text-lm-primary transition-colors"
          title="Change this answer"
        >
          <Pencil className="w-3 h-3" /> Change
        </button>
      )}
      <div className={"max-w-[85%] rounded-2xl rounded-tr-sm bg-lm-primary text-white px-4 py-2.5 shadow-sm " + (mono ? "font-mono text-sm break-all" : "")}>
        {children}
      </div>
    </div>
  );
}

export function TypingDots() {
  return (
    <span className="inline-flex gap-1">
      <span className="w-2 h-2 rounded-full bg-lm-primary animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-2 h-2 rounded-full bg-lm-primary animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-2 h-2 rounded-full bg-lm-primary animate-bounce" style={{ animationDelay: "300ms" }} />
    </span>
  );
}

/* Quick-reply container — indented under the assistant avatar */
export function QuickReplies({ children }) {
  return <div className="pl-11 animate-[fadeIn_0.3s_ease]">{children}</div>;
}
